import { z } from 'zod';
import { assetsAssetIdSchema, assetsCommandIdSchema } from './assets-asset.contract.js';
export const assetsMaintenanceCapability = 'assets.maintenance.v1';
export const assetsMaintenanceCategorySchema = z.enum([
    'issue', 'service', 'inspection', 'oil-change', 'registration', 'insurance',
]);
export const assetsMaintenanceSeveritySchema = z.enum(['low', 'normal', 'high', 'critical']);
export const assetsMaintenanceStatusSchema = z.enum(['open', 'in-progress', 'resolved', 'closed']);
export const assetsMaintenanceWitnessSchema = z.string().trim().min(1).max(160);
export const assetsMaintenanceAttachmentObjectIdSchema = z.string().uuid();
const assetsMaintenanceDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
    return date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month &&
        date.getUTCDate() === day;
}, 'Date must be a real calendar date in YYYY-MM-DD form.');
export const assetsMaintenanceInputSchema = z.strictObject({
    assetId: assetsAssetIdSchema,
    category: assetsMaintenanceCategorySchema,
    severity: assetsMaintenanceSeveritySchema,
    status: assetsMaintenanceStatusSchema,
    summary: z.string().trim().min(1).max(240),
    details: z.string().trim().min(1).max(8_000),
    assignedMembershipId: z.string().uuid().nullable().default(null),
    witnesses: z.array(assetsMaintenanceWitnessSchema).max(25).default([]),
    attachmentObjectIds: z.array(assetsMaintenanceAttachmentObjectIdSchema).max(25).default([]),
    dueDate: assetsMaintenanceDateSchema.nullable(),
    serviceDate: assetsMaintenanceDateSchema.nullable(),
    resolution: z.string().trim().min(1).max(8_000).nullable(),
    completedAt: z.string().datetime({ offset: true }).nullable(),
}).superRefine((value, context) => {
    if (new Set(value.witnesses.map((witness) => witness.toLocaleLowerCase())).size !== value.witnesses.length) {
        context.addIssue({ code: 'custom', path: ['witnesses'], message: 'Maintenance witnesses must be unique.' });
    }
    if (new Set(value.attachmentObjectIds).size !== value.attachmentObjectIds.length) {
        context.addIssue({ code: 'custom', path: ['attachmentObjectIds'], message: 'Maintenance attachment references must be unique.' });
    }
    const resolved = value.status === 'resolved' || value.status === 'closed';
    if (resolved !== (value.resolution !== null && value.completedAt !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['resolution'],
            message: 'Resolved or closed maintenance requires resolution and completion time; active work must omit both.',
        });
    }
    if (value.category === 'oil-change' && resolved && value.serviceDate === null) {
        context.addIssue({
            code: 'custom',
            path: ['serviceDate'],
            message: 'A completed oil change requires its service date.',
        });
    }
});
export const assetsMaintenanceRecordSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    record: assetsMaintenanceInputSchema,
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const assetsMaintenanceVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    recordId: z.string().uuid(),
    revision: z.number().int().positive(),
    operation: z.enum(['create', 'update', 'archive', 'restore']),
    record: assetsMaintenanceInputSchema,
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsCreateMaintenanceCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: assetsCommandIdSchema,
    recordId: z.string().uuid(),
    record: assetsMaintenanceInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsUpdateMaintenanceCommandSchema = z.strictObject({
    kind: z.literal('update'),
    commandId: assetsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    record: assetsMaintenanceInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsMaintenanceStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: assetsCommandIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assetsMaintenanceCommandSchema = z.discriminatedUnion('kind', [
    assetsUpdateMaintenanceCommandSchema,
    assetsMaintenanceStateCommandSchema,
]);
export const assetsMaintenanceListQuerySchema = z.strictObject({
    assetId: assetsAssetIdSchema.nullable().default(null),
    status: assetsMaintenanceStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export const assetsMaintenanceRecordsResponseSchema = z.strictObject({
    data: z.array(assetsMaintenanceRecordSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsMaintenanceRecordResponseSchema = z.strictObject({
    data: assetsMaintenanceRecordSchema,
    command: z.strictObject({ commandId: assetsCommandIdSchema, idempotent: z.boolean() }).optional(),
});
export const assetsMaintenanceVersionsResponseSchema = z.strictObject({
    data: z.array(assetsMaintenanceVersionSchema),
});
export const assetsMaintenanceReminderCapability = 'assets.maintenance-reminder-intents.v1';
export const assetsMaintenanceReminderKindSchema = z.enum([
    'oil-change', 'registration', 'insurance', 'maintenance-due',
]);
export const assetsMaintenanceReminderStateSchema = z.enum(['due-soon', 'overdue']);
export const assetsMaintenanceReminderRecipientSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('role'), roleKey: z.enum(['owner', 'manager']) }),
    z.strictObject({ kind: z.literal('membership'), membershipId: z.string().uuid() }),
]);
export const assetsMaintenanceReminderSnapshotSchema = z.strictObject({
    assetId: assetsAssetIdSchema,
    assetName: z.string().trim().min(1).max(240),
    assetTag: z.string().trim().min(1).max(120),
    reminderKind: assetsMaintenanceReminderKindSchema,
    reminderState: assetsMaintenanceReminderStateSchema,
    dueDate: assetsMaintenanceDateSchema.nullable(),
    currentMileage: z.number().int().nonnegative().nullable(),
    dueMileage: z.number().int().nonnegative().nullable(),
    maintenanceRecordId: z.string().uuid().nullable(),
});
export const assetsMaintenanceReminderIntentSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    intentKey: z.string().trim().min(1).max(500),
    recipient: assetsMaintenanceReminderRecipientSchema,
    snapshot: assetsMaintenanceReminderSnapshotSchema,
    scheduledFor: z.string().datetime({ offset: true }),
    configurationReleaseId: z.string().uuid().nullable(),
    requestedAt: z.string().datetime({ offset: true }),
});
export const assetsPrepareMaintenanceReminderIntentsSchema = z.strictObject({
    asOf: z.string().datetime({ offset: true }),
    assetLimit: z.number().int().min(1).max(1_000).default(500),
});
export const assetsMaintenanceReminderIntentBatchSchema = z.strictObject({
    data: z.array(assetsMaintenanceReminderIntentSchema).max(10_000),
    counts: z.record(assetsMaintenanceReminderKindSchema, z.number().int().nonnegative()),
});
//# sourceMappingURL=assets-maintenance.contract.js.map